import { useState } from 'react';
import { Plus, Webhook, Send, Trash2, Copy, Check } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { Card, Badge, EmptyState } from '../components/ui';

interface WebhookRow {
  id: string;
  url: string;
  subscribed_event_types: string[];
  status: string;
  description: string | null;
}

const EVENT_OPTIONS = [
  'clicked',
  'opened',
  'submitted_data',
  'reported',
  'bounced',
  'replied',
  'certificate_issued',
  'training_completed',
];

// Webhooks management — rendered as a tab inside the API / MCP page (no own PageHeader).
export function WebhooksSection() {
  const { data, error, reload } = useLoad(() => api.get<{ data: WebhookRow[] }>('/v1/webhooks'));
  const [url, setUrl] = useState('');
  const [types, setTypes] = useState<string[]>(['clicked', 'submitted_data']);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function toggle(t: string) {
    setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  }

  async function create() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post<{ signing_secret: string }>('/v1/webhooks', {
        url,
        subscribed_event_types: types,
      });
      setSecret(res.signing_secret);
      setCopied(false);
      setUrl('');
      reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function test(id: string) {
    setMsg(null);
    try {
      await api.post(`/v1/webhooks/${id}/test`, {});
      setMsg('Test event sent.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Test failed');
    }
  }

  async function remove(id: string) {
    await api.del(`/v1/webhooks/${id}`);
    reload();
  }

  const rows = data?.data ?? [];
  const canCreate = url && types.length > 0;

  return (
    <div>
      {(error || msg) && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          {error ?? msg}
        </div>
      )}

      {secret && (
        <div className="mb-4 rounded-xl border border-ink/20 bg-ink/[0.03] p-4">
          <div className="text-sm font-semibold text-ink">
            Signing secret — copy it now, it won’t be shown again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-white/70 px-3 py-2 font-mono text-sm text-ink">
              {secret}
            </code>
            <button
              onClick={() =>
                void navigator.clipboard?.writeText(secret).then(() => setCopied(true))
              }
              className="btn-outline shrink-0"
            >
              {copied ? <Check className="h-4 w-4 text-ink" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button onClick={() => setSecret(null)} className="mt-2 text-xs text-muted underline">
            Dismiss
          </button>
        </div>
      )}

      <Card className="mb-4 p-5">
        <div className="mb-3 text-sm font-semibold text-slate-700">New webhook</div>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">Endpoint URL</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="input"
            placeholder="https://example.com/webhooks/pavlov"
          />
        </label>
        <div className="mt-4">
          <span className="mb-2 block text-xs font-medium text-slate-500">Subscribed events</span>
          <div className="flex flex-wrap gap-2">
            {EVENT_OPTIONS.map((t) => {
              const on = types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  className={
                    on
                      ? 'rounded-full bg-ink px-3 py-1 text-xs font-medium text-white'
                      : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200'
                  }
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={() => void create()}
          disabled={busy || !canCreate}
          className="btn-primary mt-4"
        >
          <Plus className="h-4 w-4" /> Create webhook
        </button>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-5 py-3">Endpoint</th>
              <th className="px-5 py-3">Events</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((w) => (
              <tr key={w.id} className="transition-colors hover:bg-slate-50/60">
                <td className="max-w-xs truncate px-5 py-3 font-mono text-xs text-slate-600">
                  {w.url}
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {w.subscribed_event_types.slice(0, 3).map((t) => (
                      <Badge key={t} tone="outline">
                        {t}
                      </Badge>
                    ))}
                    {w.subscribed_event_types.length > 3 && (
                      <Badge tone="muted">+{w.subscribed_event_types.length - 3}</Badge>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <Badge tone="outline">{w.status}</Badge>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => void test(w.id)}
                      className="btn-outline px-2.5 py-1.5 text-xs"
                    >
                      <Send className="h-3.5 w-3.5" /> Test
                    </button>
                    <button
                      onClick={() => void remove(w.id)}
                      className="btn px-2.5 py-1.5 text-xs text-ink hover:bg-ink/[0.04]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <EmptyState
            icon={Webhook}
            title="No webhooks yet"
            hint="Add an endpoint to receive signed events."
          />
        )}
      </Card>
    </div>
  );
}
